/**
 * NeuesPlacementDialog - the «Neues Placement» create form (P2 Placement Slice 4).
 *
 * Fields (top to bottom): Anlass → Referenzpolice (shown per the Anlass rule) → Sparte
 * (Branchencode; auto-filled from a classified Referenzpolice server-side) → Objekt (only with a
 * policy) → Wunschbeginn → Betreuer (read-only «Sie»). On submit it POSTs the create and navigates
 * to the new Placement; the server owns all validation - the dialog maps the typed 400/404 codes
 * to German inline messages.
 *
 * The server is the hard line: the dialog never re-implements the anlass↔policy / Sparte-leaf /
 * subject-match rules; it only shapes the form and surfaces the error.
 */

import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Dialog } from '../administration/components';
import { ApiError } from '../../lib/api-client';
import {
  PLACEMENT_ANLASS_LABELS,
  PLACEMENT_ANLASS_VALUES,
  placementAnlassErlaubtPolice,
  isPlacementBranchencodeErlaubt,
  igb2bLeafCodesOfFamily,
  IGB2B_CODE_LABELS,
} from '@brokly/shared';
import type { PlacementAnlass, PlacementCreateBody } from '@brokly/shared';
import { useCreatePlacement } from './usePlacements';

/** A pickable Referenzpolice (the minimal shape the Kunde Policen list already carries). */
export interface PlacementPolicyOption {
  id: string;
  nr: string;
  versicherer?: string;
  /** The policy's IGB2B Sparte (`policy.igb2b_code`); null on an unclassified policy. */
  igb2b_code: string | null;
}

/** Maps the service's typed error codes to a German inline message. */
const ERROR_MESSAGES: Record<string, string> = {
  anlass_requires_policy: 'Für diesen Anlass wird eine Referenzpolice benötigt.',
  anlass_forbids_policy: 'Ein Neugeschaeft darf keine Referenzpolice haben.',
  object_ref_without_policy: 'Ein Objekt-Bezug ist nur mit einer Referenzpolice möglich.',
  branchencode_required: 'Bitte eine Sparte (Branchencode) angeben.',
  branchencode_mismatch: 'Die Sparte weicht von der Referenzpolice ab.',
  branchencode_not_leaf: 'Bitte eine konkrete Sparte wählen (kein Oberbegriff).',
  invalid_branchencode: 'Unbekannte Sparte.',
  customer_not_found: 'Kunde nicht gefunden.',
  policy_not_found: 'Referenzpolice nicht gefunden.',
  policy_subject_mismatch: 'Die Referenzpolice gehört zu einem anderen Kunden.',
  actor_unresolved: 'Der Benutzer konnte nicht aufgelöst werden.',
  number_conflict: 'Nummernkonflikt, bitte erneut versuchen.',
};

const inputClass =
  'w-full rounded-md border bg-card px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function NeuesPlacementDialog({
  open,
  onClose,
  subjectType,
  subjectId,
  policen,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  subjectType: 'person' | 'firma';
  subjectId: string;
  policen: PlacementPolicyOption[];
  onCreated: (id: string) => void;
}) {
  const create = useCreatePlacement();
  const [anlass, setAnlass] = useState<PlacementAnlass>('neugeschaeft');
  const [policyId, setPolicyId] = useState<string>('');
  const [branchencode, setBranchencode] = useState<string>('');
  const [objektRef, setObjektRef] = useState<string>('');
  const [beginn, setBeginn] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const policeRule = placementAnlassErlaubtPolice(anlass);
  const showPolice = policeRule !== 'forbidden';
  const hasPolicy = showPolice && policyId !== '';

  // The selected Referenzpolice's Sparte drives how the Branchencode field behaves:
  //   - leaf-coded (or 60000): prefill + lock the Sparte to the policy's code.
  //   - family-head coded: the policy only names the family → offer the family's concrete leaves.
  //   - unclassified (null) / no policy: a free leaf input (the server validates).
  const selectedPolicy = hasPolicy ? policen.find((p) => p.id === policyId) ?? null : null;
  const policyCode = selectedPolicy?.igb2b_code ?? null;
  const spartenLock = policyCode !== null && isPlacementBranchencodeErlaubt(policyCode);
  const familyLeaves =
    policyCode !== null && !isPlacementBranchencodeErlaubt(policyCode)
      ? igb2bLeafCodesOfFamily(policyCode, Object.keys(IGB2B_CODE_LABELS))
      : null;

  // Sync the Branchencode to the selection: lock → the policy's code; otherwise clear (free pick).
  useEffect(() => {
    setBranchencode(spartenLock && policyCode ? policyCode : '');
  }, [policyId, policyCode, spartenLock]);

  const reset = () => {
    setAnlass('neugeschaeft');
    setPolicyId('');
    setBranchencode('');
    setObjektRef('');
    setBeginn('');
    setError(null);
  };

  const close = () => {
    if (create.isPending) return;
    reset();
    onClose();
  };

  async function submit() {
    setError(null);
    const body: PlacementCreateBody = {
      subject_type: subjectType,
      subject_id: subjectId,
      anlass,
      ...(hasPolicy ? { policy_id: policyId } : {}),
      ...(branchencode.trim() ? { branchencode: branchencode.trim() } : {}),
      ...(hasPolicy && objektRef.trim() ? { insurance_object_ref: objektRef.trim() } : {}),
      ...(beginn ? { gewuenschter_beginn: beginn } : {}),
    };
    try {
      const detail = await create.mutateAsync(body);
      reset();
      onClose();
      onCreated(detail.id);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : undefined;
      setError((code && ERROR_MESSAGES[code]) || (err instanceof Error ? err.message : 'Placement konnte nicht erstellt werden.'));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Neues Placement"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={create.isPending}>
            Abbrechen
          </Button>
          <Button onClick={() => void submit()} disabled={create.isPending} data-testid="np-submit">
            {create.isPending ? 'Wird erstellt …' : 'Erstellen'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4" data-testid="neues-placement-dialog">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Anlass</span>
          <select
            className={inputClass}
            value={anlass}
            data-testid="np-anlass"
            onChange={(e) => {
              setAnlass(e.target.value as PlacementAnlass);
              setPolicyId('');
              setObjektRef('');
            }}
          >
            {PLACEMENT_ANLASS_VALUES.map((a) => (
              <option key={a} value={a}>
                {PLACEMENT_ANLASS_LABELS[a]}
              </option>
            ))}
          </select>
        </label>

        {showPolice && (
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">
              Referenzpolice{policeRule === 'required' ? '' : ' (optional)'}
            </span>
            <select
              className={inputClass}
              value={policyId}
              data-testid="np-policy"
              onChange={(e) => setPolicyId(e.target.value)}
            >
              <option value="">- keine -</option>
              {policen.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nr}
                  {p.versicherer ? ` · ${p.versicherer}` : ''}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Sparte (Branchencode)</span>
          {spartenLock ? (
            <input
              className={inputClass}
              value={branchencode}
              readOnly
              aria-readonly="true"
              data-testid="np-branchencode"
            />
          ) : familyLeaves ? (
            <select
              className={inputClass}
              value={branchencode}
              data-testid="np-branchencode"
              onChange={(e) => setBranchencode(e.target.value)}
            >
              <option value="">- Sparte wählen -</option>
              {familyLeaves.map((c) => (
                <option key={c} value={c}>
                  {c} · {IGB2B_CODE_LABELS[c]}
                </option>
              ))}
            </select>
          ) : (
            <input
              className={inputClass}
              value={branchencode}
              inputMode="numeric"
              placeholder="z. B. 21000"
              data-testid="np-branchencode"
              onChange={(e) => setBranchencode(e.target.value)}
            />
          )}
        </label>

        {hasPolicy && (
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Versichertes Objekt (optional)</span>
            <input
              className={inputClass}
              value={objektRef}
              placeholder="z. B. Fahrzeug 1"
              data-testid="np-objekt"
              onChange={(e) => setObjektRef(e.target.value)}
            />
          </label>
        )}

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Gewünschter Beginn (optional)</span>
          <input
            type="date"
            className={inputClass}
            value={beginn}
            data-testid="np-beginn"
            onChange={(e) => setBeginn(e.target.value)}
          />
        </label>

        <div className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Betreuer</span>
          <span className="text-muted-foreground">Sie</span>
        </div>

        {error && (
          <p role="alert" className="text-sm text-status-danger" data-testid="np-error">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
